import { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { Agentation } from 'agentation';
import MainLayout from './layouts/MainLayout';
import {
  LoginPage,
  RegisterPage,
  AuthCallbackPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  authApi,
} from '@/features/auth';
import i18n from './i18n/config';
import Portfolio from './pages/portfolio';
import AuthRedirect from './components/common/AuthRedirect';
import AuthenticatedRedirect from './components/common/AuthenticatedRedirect';
import { TermsGuard } from './components/common/TermsGuard';
import { flattenRoutes, type RouteConfig } from './config/routes';
import { useAuthStore } from './stores';
import { PUBLIC_ROUTES, DEFAULT_AUTHENTICATED_ROUTE } from './constants/routes';

import { usePageTracking } from '@/hooks/usePageTracking';
import { useIsSuperAdmin } from '@/hooks';
import { isDemoMode } from '@/config/demo';
import { isBillingEnabled } from '@/config/features';
import './App.css';

// Check if user has required role for a route
const hasRequiredRole = (route: RouteConfig, userRole?: string, isSuperAdmin = false): boolean => {
  if (!route.roles || route.roles.length === 0) return true;
  if (route.roles.includes('super_admin') && isSuperAdmin) return true;
  return !!userRole && route.roles.includes(userRole);
};

// Check if user's email is allowed for a route (email allowlist gate)
const hasAllowedEmail = (route: RouteConfig, userEmail?: string): boolean => {
  if (isDemoMode) return true;
  if (!route.allowedEmails || route.allowedEmails.length === 0) return true;
  if (!userEmail) return false;
  const normalized = userEmail.trim().toLowerCase();
  return route.allowedEmails.some((email) => email.trim().toLowerCase() === normalized);
};

// Check billing master switch for routes flagged requiresBilling
const passesBillingGate = (route: RouteConfig): boolean =>
  !route.requiresBilling || isBillingEnabled;

// Combined route access check (role + email allowlist + billing switch)
const canAccessRoute = (
  route: RouteConfig,
  userRole?: string,
  userEmail?: string,
  isSuperAdmin = false
): boolean =>
  hasRequiredRole(route, userRole, isSuperAdmin) &&
  hasAllowedEmail(route, userEmail) &&
  passesBillingGate(route);

const LoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height="200px">
    <CircularProgress />
  </Box>
);

function App() {
  usePageTracking();

  const {
    isAuthenticated,
    accessToken,
    refreshToken,
    hasHydrated,
    setInitializing,
    setUser,
    clearAuth,
    setTokenAuth,
    user,
  } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const isSuperAdmin = useIsSuperAdmin();

  // App initialization - Wait for hydration then verify token
  useEffect(() => {
    // Wait for Zustand to hydrate from localStorage
    if (!hasHydrated) {
      return;
    }

    const initializeAuth = async () => {
      try {
        // If no accessToken, complete initialization directly
        if (!accessToken) {
          setInitializing(false);
          setIsInitialized(true);
          return;
        }

        // Attempt to get user profile with current token
        try {
          const userProfile = await authApi.getProfile();
          setUser(userProfile);
          // 從 profile.settings 讀取語言設定並套用
          if (userProfile.settings?.lang && userProfile.settings.lang !== i18n.language) {
            i18n.changeLanguage(userProfile.settings.lang);
            localStorage.setItem('i18nextLng', userProfile.settings.lang);
          }
        } catch {
          // Token verification failed, attempt to refresh
          if (refreshToken) {
            try {
              const newTokens = await authApi.refreshToken();
              setTokenAuth({
                access_token: newTokens.access_token,
                refresh_token: newTokens.refresh_token,
              });

              // Re-fetch profile after successful refresh
              const userProfile = await authApi.getProfile();
              setUser(userProfile);
              // 從 profile.settings 讀取語言設定並套用
              if (userProfile.settings?.lang && userProfile.settings.lang !== i18n.language) {
                i18n.changeLanguage(userProfile.settings.lang);
                localStorage.setItem('i18nextLng', userProfile.settings.lang);
              }
              console.log('✅ Token refreshed and profile fetched successfully');
            } catch (refreshError) {
              // Refresh failed, clear authentication state
              console.error('❌ Token refresh failed:', refreshError);
              clearAuth();
            }
          } else {
            // No refreshToken, clear authentication state
            console.error('❌ Token verification failed and no refresh token available');
            clearAuth();
          }
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        clearAuth();
      } finally {
        setInitializing(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated]);

  const userAuthenticated = isAuthenticated();

  // Wait for initialization to complete before showing the app
  if (!isInitialized) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <TermsGuard />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path={PUBLIC_ROUTES.PORTFOLIO} element={<Portfolio />} />
          <Route
            path={PUBLIC_ROUTES.LOGIN}
            element={userAuthenticated ? <AuthenticatedRedirect /> : <LoginPage />}
          />
          <Route
            path={PUBLIC_ROUTES.REGISTER}
            element={userAuthenticated ? <AuthenticatedRedirect /> : <RegisterPage />}
          />
          <Route path={PUBLIC_ROUTES.AUTH_CALLBACK} element={<AuthCallbackPage />} />
          <Route
            path={PUBLIC_ROUTES.FORGOT_PASSWORD}
            element={userAuthenticated ? <AuthenticatedRedirect /> : <ForgotPasswordPage />}
          />
          <Route
            path={PUBLIC_ROUTES.RESET_PASSWORD}
            element={userAuthenticated ? <AuthenticatedRedirect /> : <ResetPasswordPage />}
          />

          {/* Semi-Public Routes - Accessible without auth but with feature gates */}
          <Route element={<MainLayout />}>
            {flattenRoutes()
              .filter(
                (route) => route.isPublic && route.path !== '/login' && route.path !== '/register'
              )
              .map((route) => (
                <Route key={route.path} path={route.path} element={route.element} />
              ))}
          </Route>

          {/* Protected Routes - Authentication required */}
          <Route element={userAuthenticated ? <MainLayout /> : <AuthRedirect />}>
            {flattenRoutes()
              .filter(
                (route) => !route.isPublic && route.path !== '/login' && route.path !== '/register'
              )
              .map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    canAccessRoute(route, user?.role, user?.email, isSuperAdmin) ? (
                      route.element
                    ) : (
                      <Navigate to={DEFAULT_AUTHENTICATED_ROUTE} replace />
                    )
                  }
                />
              ))}
          </Route>
        </Routes>
      </Suspense>
      {import.meta.env.DEV && <Agentation />}
    </>
  );
}

export default App;
